import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import {
  useController,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  CompositeMenuButton,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingSelect, { SelectCore } from '#/modules/commonComponents/form/select';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import {
  ScanCodeDialog,
  ScanTriggerButton,
} from '#/modules/commonComponents/scan';
import StockReceiptBatchAddPackagesDialog from '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptBatchAddPackagesDialog';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import {
  getOriginalSerialItemId,
  lookupOriginalSerial,
  ORIGINAL_SERIAL_SCAN_LABELS,
  renderOriginalSerialAmbiguousRow,
  renderOriginalSerialScannedRow,
} from '#/modules/imsMgmt/receipts/shared/scan';
import { useMaterialUnitOptions } from '#/modules/imsMgmt/helper/materialUnit';
import {
  calcLineAmount,
  createEmptyPackage,
  formatMoneyLabel,
  mapMtrPickerRowToReceiptLine,
  mapNewMtrPickerRowsToReceiptLines,
  sumLineQty,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/formSerialize';
import { getStockReceiptApiErrorMessage } from '#/modules/imsMgmt/receipts/stockReceipt/helper/apiError';
import { fetchWarehouseLocationSelectOptions } from '#/modules/imsMgmt/receipts/stockReceipt/helper/locationSelectOptions';
import {
  calcLineUnitPrice,
  formatUnitPriceResidual,
  PRICE_CALC_MODE,
  PRICE_CALC_MODE_OPTIONS,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/unitPriceFromTotal';
import { STOCK_RECEIPT_LINES_TIP } from '#/modules/imsMgmt/receipts/stockReceipt/schemas/stockReceiptSchema';
import MtrPickerDialog from '#/modules/imsMgmt/settings/mtr/components/MtrPickerDialog';
import { lookupQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const PKG_GRID_COLS =
  '@md/pkg:grid-cols-[2.5rem_minmax(4.5rem,6rem)_minmax(7rem,1fr)_minmax(6rem,1fr)_2.5rem]';

const PKG_HEADER_CLASS = cn(
  'sticky top-0 z-[1] hidden w-full gap-2 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@md/pkg:grid',
  PKG_GRID_COLS,
);

const PKG_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-2 px-3 py-3',
  'border-b border-gray-200 last:border-b-0',
  '@md/pkg:items-center',
  PKG_GRID_COLS,
);

const StockReceiptMaterialSelectList = ({
  lines,
  selectedMtrId,
  onSelect,
  onRemove,
  showCommercialLinePricing = true,
}) => (
  <div className="form-list form-list--window">
    <div className="form-list__head">
      <p className="font-medium">已選擇物料</p>
      <p className="text-xs text-gray-500">
        {lines.length}
        {' '}
        筆
      </p>
    </div>
    <div className="form-list__body">
      {lines.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          尚未選擇物料，請點「選擇物料」新增。
        </p>
      ) : (
        lines.map((line, index) => {
          const isActive = line.mtrId && line.mtrId === selectedMtrId;
          const qty = sumLineQty(line);
          const amount = calcLineAmount(line);
          const stackName = line.mtrName
            || line.mtrNo
            || (line.mtrId ? String(line.mtrId).slice(-8) : '');
          const stackCode = line.mtrNo || line.mtrId || '';

          return (
            <div
              key={line.id ?? `${line.mtrId}-${index}`}
              className={cn(
                '@container/material-line',
                'cursor-pointer px-4 py-2.5 hover:bg-gray-50',
                isActive && 'bg-primary-50',
              )}
              onClick={() => onSelect(line.mtrId)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(line.mtrId);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div
                className={cn(
                  'grid grid-cols-1 items-center gap-3',
                  '@lg/material-line:grid-cols-[minmax(0,1fr)_auto_auto]',
                )}
              >
                <MaterialNameStack name={stackName} code={stackCode} />
                <div
                  className={cn(
                    'text-xs text-gray-500',
                    '@lg/material-line:text-right',
                  )}
                >
                  <div className="space-x-1">
                    <span className="text-gray-500">數量 </span>
                    <span className="text-sm font-medium text-gray-900">
                      {qty.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div
                  className={cn(
                    'flex items-center gap-2 text-xs text-gray-500',
                    '@lg/material-line:justify-end',
                  )}
                >
                  {showCommercialLinePricing ? (
                    <div className="space-x-1">
                      <span className="text-gray-500">金額 </span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatMoneyLabel(amount)}
                      </span>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-600"
                    aria-label="移除此物料"
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemove(index);
                    }}
                  >
                    <PiTrashBold size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  </div>
);

StockReceiptMaterialSelectList.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedMtrId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showCommercialLinePricing: PropTypes.bool,
};

const StockReceiptMaterialSettingFields = ({
  control,
  register,
  errors,
  setValue,
  lineIndex,
  mtrId = '',
  mtrName = '',
  mtrNo = '',
  warehouseId,
  locationOptions,
  locationOptionsLoading,
  initialUnitOptions = null,
  showCommercialLinePricing = true,
}) => {
  const [batchOpen, setBatchOpen] = useState(false);
  const appliedReceiptDefaultQtyMtrIdRef = useRef('');
  const {
    fields: packageFields,
    append: appendPackage,
    remove: removePackage,
  } = useFieldArray({
    control,
    name: `lines.${lineIndex}.packages`,
  });

  const watchedPackages = useWatch({
    control,
    name: `lines.${lineIndex}.packages`,
    defaultValue: [],
  });
  const priceCalcMode = useWatch({
    control,
    name: `lines.${lineIndex}.priceCalcMode`,
    defaultValue: PRICE_CALC_MODE.FROM_UNIT_PRICE,
  });
  const priceTotal = useWatch({
    control,
    name: `lines.${lineIndex}.priceTotal`,
    defaultValue: '',
  });
  const priceInput = useWatch({
    control,
    name: `lines.${lineIndex}.priceInput`,
    defaultValue: '',
  });
  const unitId = useWatch({
    control,
    name: `lines.${lineIndex}.unitId`,
    defaultValue: '',
  });
  const unitPrice = useWatch({
    control,
    name: `lines.${lineIndex}.unitPrice`,
    defaultValue: '',
  });

  const {
    options: unitOptions,
    selectOptions: unitSelectOptions,
    defaultUnitId,
    receiptDefaultQty,
    isLoading: unitOptionsLoading,
  } = useMaterialUnitOptions({
    mtrId,
    initialOptions: initialUnitOptions,
    enabled: Boolean(mtrId),
  });

  const unitDisabled = !mtrId || unitOptionsLoading;
  const isFromTotal = priceCalcMode === PRICE_CALC_MODE.FROM_TOTAL;
  const isFromUnitPrice = priceCalcMode === PRICE_CALC_MODE.FROM_UNIT_PRICE;

  const priceCalcResult = useMemo(
    () => calcLineUnitPrice({
      priceCalcMode,
      priceTotal,
      priceInput,
      packages: watchedPackages,
    }),
    [
      priceCalcMode,
      priceTotal,
      priceInput,
      watchedPackages,
    ],
  );

  const unitPriceResidual = priceCalcResult.residual;
  const modeInputEmpty = (
    (isFromTotal && (priceTotal === '' || priceTotal == null))
    || (isFromUnitPrice && (priceInput === '' || priceInput == null))
  );

  useEffect(() => {
    if (!mtrId || !defaultUnitId || unitOptionsLoading) {
      return;
    }

    setValue(`lines.${lineIndex}.unitOptions`, unitOptions, {
      shouldDirty: false,
      shouldValidate: false,
    });

    if (!String(unitId ?? '').trim()) {
      setValue(`lines.${lineIndex}.unitId`, defaultUnitId, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    // 入庫預設數量：每物料只預填一次；不跟 convert_rate 聯動重算
    if (appliedReceiptDefaultQtyMtrIdRef.current !== mtrId) {
      const packages = watchedPackages ?? [];
      const onlyEmptyPkg = packages.length === 1
        && (packages[0]?.qty === '' || packages[0]?.qty == null);
      const hasDefaultQty = receiptDefaultQty !== ''
        && receiptDefaultQty != null
        && Number.isFinite(Number(receiptDefaultQty));
      if (onlyEmptyPkg && hasDefaultQty) {
        setValue(`lines.${lineIndex}.packages.0.qty`, Number(receiptDefaultQty), {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      appliedReceiptDefaultQtyMtrIdRef.current = mtrId;
    }
  }, [
    mtrId,
    defaultUnitId,
    unitOptionsLoading,
    unitOptions,
    lineIndex,
    setValue,
    unitId,
    receiptDefaultQty,
    watchedPackages,
  ]);

  useEffect(() => {
    if (!showCommercialLinePricing) {
      return;
    }

    const nextUnitPrice = priceCalcResult.unitPrice;
    const current = unitPrice === '' || unitPrice == null
      ? ''
      : Number(unitPrice);

    if (modeInputEmpty) {
      if (unitPrice !== '' && unitPrice != null) {
        setValue(`lines.${lineIndex}.unitPrice`, '', {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
      return;
    }

    if (nextUnitPrice == null) {
      return;
    }

    if (Number(current) === Number(nextUnitPrice)) {
      return;
    }

    setValue(`lines.${lineIndex}.unitPrice`, nextUnitPrice, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [
    showCommercialLinePricing,
    priceCalcResult.unitPrice,
    modeInputEmpty,
    unitPrice,
    lineIndex,
    setValue,
  ]);

  const locationDisabled = !warehouseId;
  const stackName = mtrName
    || mtrNo
    || '';
  const stackCode = mtrNo || '';
  const hasMaterialIdentity = Boolean(stackName || stackCode);

  function handleAddSingle() {
    appendPackage(createEmptyPackage());
  }

  function handleBatchConfirm(packages) {
    appendPackage(packages);
  }

  const unitPriceFieldName = `lines.${lineIndex}.unitPrice`;
  const priceCalcModeName = `lines.${lineIndex}.priceCalcMode`;
  const priceTotalName = `lines.${lineIndex}.priceTotal`;
  const priceInputName = `lines.${lineIndex}.priceInput`;
  const unitIdName = `lines.${lineIndex}.unitId`;

  const unitSelectField = (
    <FloatingSelect
      id={unitIdName}
      label="單位"
      options={unitSelectOptions}
      control={control}
      errors={errors}
      isDisabled={unitDisabled}
      noOptionsMessage={
        !mtrId ? '請先選擇物料' : '此物料尚無可選單位'
      }
      containerClassName="w-full"
    />
  );

  const unitPriceField = (
    <FloatingInput
      id={unitPriceFieldName}
      label="單價"
      type="number"
      register={register}
      errors={errors}
      readOnly
      containerClassName={cn(
        'w-full',
        '@2xl/sheet:col-span-2 @4xl/sheet:col-span-1',
      )}
    />
  );

  const residualHint = unitPriceResidual != null && Number(unitPriceResidual) > 0 ? (
    <p className="text-xs text-amber-700">
      回算誤差：
      {formatUnitPriceResidual(unitPriceResidual)}
      （僅顯示，不會儲存資料）
    </p>
  ) : null;

  return (
    <div className="form-list form-list--window">
      <div
        className={cn(
          'shrink-0 px-5 py-2.5 bg-gray-300 rounded-t-md',
          'text-sm font-semibold text-gray-700',
        )}
      >
        物料設定
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="p-4 space-y-3">
          <div className="space-y-1">
            <p className="text-xs text-gray-500">物料名稱/物料料號</p>
            {hasMaterialIdentity ? (
              <MaterialNameStack name={stackName} code={stackCode} />
            ) : (
              <p className="text-sm text-gray-700">—</p>
            )}
          </div>

          {showCommercialLinePricing ? (
            <>
              <FloatingSelect
                id={priceCalcModeName}
                label="計算方式"
                options={PRICE_CALC_MODE_OPTIONS}
                control={control}
                errors={errors}
                containerClassName="w-full"
              />

              {isFromTotal ? (
                <div className="space-y-1">
                  <div
                    className={cn(
                      'grid grid-cols-1 gap-3',
                      '@2xl/sheet:grid-cols-2 @4xl/sheet:grid-cols-3',
                    )}
                  >
                    {unitPriceField}
                    {unitSelectField}
                    <FloatingInput
                      id={priceTotalName}
                      label="總額"
                      type="number"
                      register={register}
                      errors={errors}
                      containerClassName="w-full"
                    />
                  </div>
                  {residualHint}
                </div>
              ) : null}

              {isFromUnitPrice ? (
                <div
                  className={cn(
                    'grid grid-cols-1 gap-3',
                    '@2xl/sheet:grid-cols-2',
                  )}
                >
                  <FloatingInput
                    id={priceInputName}
                    label="單價"
                    type="number"
                    register={register}
                    errors={errors}
                    containerClassName="w-full"
                  />
                  {unitSelectField}
                </div>
              ) : null}
            </>
          ) : (
            <div
              className={cn(
                'grid grid-cols-1 gap-3',
                '@2xl/sheet:grid-cols-2',
              )}
            >
              <FloatingInput
                id={unitPriceFieldName}
                label="單價"
                type="number"
                register={register}
                errors={errors}
                containerClassName="w-full"
              />
              {unitSelectField}
            </div>
          )}

          <FloatingTextarea
            id={`lines.${lineIndex}.remark`}
            label="明細備註"
            register={register}
            errors={errors}
            rows={2}
            containerClassName="w-full"
          />
        </div>

        <div
          className={cn(
            'px-4 py-3',
            'bg-panel border-y border-gray-300',
            'flex flex-wrap items-center gap-2',
            'sticky top-0 z-10',
          )}
        >
          <p className="form-subsection__title mb-0! mr-2">包裝</p>
          <CompositeMenuButton
            variant="default"
            label="新增包裝"
            caretTooltip="批次新增：一次加入多筆相同規格的包裝"
            caretAriaLabel="更多新增包裝選項"
            items={[
              {
                id: 'single',
                label: '新增單包裝',
                onSelect: handleAddSingle,
              },
              {
                id: 'batch',
                label: '批次新增',
                onSelect: () => setBatchOpen(true),
              },
            ]}
          />
        </div>

        <div className="@container/pkg p-2">
          <div className={PKG_HEADER_CLASS} aria-hidden>
            <span className="min-w-0 text-center">列</span>
            <span className="min-w-0">數量</span>
            <span className="min-w-0">儲位</span>
            <span className="min-w-0">包裝備註</span>
            <span className="sr-only min-w-0">操作</span>
          </div>
          <ul className="m-0 list-none p-0">
            {packageFields.map((pkgField, pkgIdx) => {
              const locationId = `lines.${lineIndex}.packages.${pkgIdx}.locationId`;
              const qtyId = `lines.${lineIndex}.packages.${pkgIdx}.qty`;
              const remarkId = `lines.${lineIndex}.packages.${pkgIdx}.remark`;
              const locationError = getNestedError(errors, locationId);

              return (
                <PackageListRow
                  key={pkgField.id}
                  pkgIdx={pkgIdx}
                  control={control}
                  register={register}
                  errors={errors}
                  locationId={locationId}
                  qtyId={qtyId}
                  remarkId={remarkId}
                  locationError={locationError}
                  locationOptions={locationOptions}
                  locationDisabled={locationDisabled || locationOptionsLoading}
                  canRemove={packageFields.length > 1}
                  onRemove={() => removePackage(pkgIdx)}
                />
              );
            })}
          </ul>
        </div>
      </div>

      <StockReceiptBatchAddPackagesDialog
        open={batchOpen}
        onOpenChange={setBatchOpen}
        onConfirm={handleBatchConfirm}
        warehouseId={warehouseId}
        locationOptions={locationOptions}
        locationOptionsLoading={locationOptionsLoading}
      />
    </div>
  );
};

StockReceiptMaterialSettingFields.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  lineIndex: PropTypes.number.isRequired,
  mtrId: PropTypes.string,
  mtrName: PropTypes.string,
  mtrNo: PropTypes.string,
  warehouseId: PropTypes.string,
  locationOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  locationOptionsLoading: PropTypes.bool,
  initialUnitOptions: PropTypes.arrayOf(PropTypes.object),
  showCommercialLinePricing: PropTypes.bool,
};

const PackageListRow = ({
  pkgIdx,
  control,
  register,
  errors,
  locationId,
  qtyId,
  remarkId,
  locationError,
  locationOptions,
  locationDisabled,
  canRemove,
  onRemove,
}) => {
  const { field: locationField } = useController({
    name: locationId,
    control,
    defaultValue: '',
  });

  return (
    <li className={PKG_ROW_CLASS}>
      <div className="min-w-0 @md/pkg:self-center @md/pkg:text-center">
        <p className="@md/pkg:hidden text-xs text-gray-500">列</p>
        <p className="text-sm font-medium text-gray-700">
          {pkgIdx + 1}
          .
        </p>
      </div>
      <div className="min-w-0">
        <PlainRegisteredInput
          id={qtyId}
          type="number"
          register={register}
          errors={errors}
          mobileLabel="數量"
          mobileLabelClassName="@md/pkg:hidden"
          ariaLabel={`數量 ${pkgIdx + 1}`}
          suppressInlineError
        />
      </div>
      <div className="min-w-0">
        <ListFieldMobileLabel
          hasError={Boolean(locationError)}
          className="@md/pkg:hidden"
        >
          儲位
        </ListFieldMobileLabel>
        <SelectCore
          id={locationId}
          options={locationOptions}
          value={locationField.value}
          onChange={locationField.onChange}
          onBlur={locationField.onBlur}
          isDisabled={locationDisabled}
          noOptionsMessage={
            locationDisabled ? '請先選擇倉別' : '此倉別尚無儲位'
          }
          placeholder="請選擇"
          errors={errors}
          hideErrorMessage
          isClearable
          aria-label={`儲位 ${pkgIdx + 1}`}
          aria-invalid={locationError ? true : undefined}
          title={locationError?.message || undefined}
          className="w-full"
        />
      </div>
      <div className="min-w-0">
        <PlainRegisteredInput
          id={remarkId}
          type="text"
          register={register}
          errors={errors}
          mobileLabel="包裝備註"
          mobileLabelClassName="@md/pkg:hidden"
          ariaLabel={`包裝備註 ${pkgIdx + 1}`}
          suppressInlineError
        />
      </div>
      <div className="flex min-w-0 items-center justify-end">
        {canRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
            aria-label={`移除包裝 ${pkgIdx + 1}`}
            onClick={onRemove}
          >
            <PiTrashBold className="size-4" />
          </Button>
        ) : null}
      </div>
    </li>
  );
};

PackageListRow.propTypes = {
  pkgIdx: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  locationId: PropTypes.string.isRequired,
  qtyId: PropTypes.string.isRequired,
  remarkId: PropTypes.string.isRequired,
  locationError: PropTypes.object,
  locationOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  locationDisabled: PropTypes.bool,
  canRemove: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
};

const StockReceiptMaterialSetting = ({
  control,
  register,
  errors,
  setValue,
  lineIndex,
  mtrId = '',
  mtrName = '',
  mtrNo = '',
  warehouseId,
  locationOptions,
  locationOptionsLoading,
  initialUnitOptions = null,
  showCommercialLinePricing = true,
}) => {
  if (lineIndex < 0) {
    return (
      <div className="form-list form-list--window">
        <div
          className={cn(
            'shrink-0 px-5 py-2.5 bg-gray-300 rounded-t-md',
            'text-sm font-semibold text-gray-700',
          )}
        >
          物料設定
        </div>
        <div className="p-4 space-y-3">
          <div className="space-y-1">
            <p className="text-xs text-gray-500">物料名稱/物料料號</p>
            <p className="text-sm text-gray-700">—</p>
          </div>
          <p className="text-sm text-muted-foreground">請選擇物料進行設定</p>
        </div>
      </div>
    );
  }

  return (
    <StockReceiptMaterialSettingFields
      control={control}
      register={register}
      errors={errors}
      setValue={setValue}
      lineIndex={lineIndex}
      mtrId={mtrId}
      mtrName={mtrName}
      mtrNo={mtrNo}
      warehouseId={warehouseId}
      locationOptions={locationOptions}
      locationOptionsLoading={locationOptionsLoading}
      initialUnitOptions={initialUnitOptions}
      showCommercialLinePricing={showCommercialLinePricing}
    />
  );
};

StockReceiptMaterialSetting.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  lineIndex: PropTypes.number.isRequired,
  mtrId: PropTypes.string,
  mtrName: PropTypes.string,
  mtrNo: PropTypes.string,
  warehouseId: PropTypes.string,
  locationOptions: PropTypes.array,
  locationOptionsLoading: PropTypes.bool,
  initialUnitOptions: PropTypes.arrayOf(PropTypes.object),
  showCommercialLinePricing: PropTypes.bool,
};

/**
 * 入庫選料雙欄：左清單（點選／刪除）＋右單價／備註／包裝。
 */
const StockReceiptLinesEditor = ({
  control,
  register,
  errors,
  setValue,
  showCommercialLinePricing = true,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  const [originalSerialScanOpen, setOriginalSerialScanOpen] = useState(false);
  const [originalSerialScanKey, setOriginalSerialScanKey] = useState(0);
  const [selectedMtrId, setSelectedMaterialId] = useState(null);
  const prevWarehouseIdRef = useRef(undefined);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lines',
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });
  const warehouseId = useWatch({ control, name: 'warehouseId', defaultValue: '' });

  const locationOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseLocationSelect', { warehouseId }),
    queryFn: () => fetchWarehouseLocationSelectOptions(warehouseId),
    enabled: Boolean(warehouseId),
  });

  const linesForRender = useMemo(() => (
    (fields ?? []).map((field, index) => {
      const watched = watchedLines?.[index] ?? {};
      return {
        ...field,
        ...watched,
        id: field.id,
        mtrId: watched.mtrId ?? field.mtrId ?? '',
        mtrNo: watched.mtrNo ?? field.mtrNo ?? '',
        mtrName: watched.mtrName ?? field.mtrName ?? '',
      };
    })
  ), [fields, watchedLines]);

  const selectedLineIndex = useMemo(
    () => linesForRender.findIndex((line) => line.mtrId === selectedMtrId),
    [linesForRender, selectedMtrId],
  );

  const initialSelectedRows = useMemo(
    () => linesForRender
      .filter((line) => line.mtrId)
      .map((line) => ({
        optionMtrId: line.mtrId,
        mtrNo: line.mtrNo ?? '',
        name: line.mtrName ?? '',
        status: '',
      })),
    [linesForRender],
  );

  useEffect(() => {
    if (prevWarehouseIdRef.current === undefined) {
      prevWarehouseIdRef.current = warehouseId;
      return;
    }
    if (prevWarehouseIdRef.current === warehouseId) {
      return;
    }
    prevWarehouseIdRef.current = warehouseId;

    linesForRender.forEach((line, lineIndex) => {
      (line.packages ?? []).forEach((_, pkgIndex) => {
        setValue(`lines.${lineIndex}.packages.${pkgIndex}.locationId`, '', {
          shouldDirty: true,
          shouldValidate: false,
        });
      });
    });
  }, [warehouseId, linesForRender, setValue]);

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handleOpenOriginalSerialScan() {
    setOriginalSerialScanKey((prev) => prev + 1);
    setOriginalSerialScanOpen(true);
  }

  function handlePickerConfirm(selectedRows) {
    const newLines = mapNewMtrPickerRowsToReceiptLines(
      linesForRender.map((line) => line.mtrId).filter(Boolean),
      selectedRows,
    );
    newLines.forEach((line) => append(line));
  }

  function handleOriginalSerialScanConfirm(selectedRows) {
    const existingIds = new Set(
      linesForRender.map((line) => line.mtrId).filter(Boolean),
    );
    let lastAddedId = null;

    selectedRows.forEach((row) => {
      const mtrId = row?.optionMtrId ?? row?.id;
      if (!mtrId) {
        return;
      }
      if (existingIds.has(mtrId)) {
        toast.error('此物料已在明細中，已跳過');
        return;
      }
      const line = mapMtrPickerRowToReceiptLine(row);
      if (line) {
        append(line);
        existingIds.add(mtrId);
        lastAddedId = mtrId;
      }
    });

    if (lastAddedId) {
      setSelectedMaterialId(lastAddedId);
    }
  }

  function handleRemove(index) {
    const removedId = linesForRender[index]?.mtrId;
    remove(index);
    if (removedId && removedId === selectedMtrId) {
      setSelectedMaterialId(null);
    }
  }

  return (
    <section>
      <div className="mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="form-block__title mb-0!">入庫明細</h2>
          <Button
            type="button"
            size="sm"
            onClick={handleOpenPicker}
          >
            選擇物料
          </Button>
          <ScanTriggerButton
            labels={ORIGINAL_SERIAL_SCAN_LABELS}
            onClick={handleOpenOriginalSerialScan}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{STOCK_RECEIPT_LINES_TIP}</p>
      </div>

      {errors?.lines?.message ? (
        <p className="mb-3 text-sm text-red-500">{errors.lines.message}</p>
      ) : null}

      <div className="sheet_2block-container sheet_2block-container--receipt">
        <StockReceiptMaterialSelectList
          lines={linesForRender}
          selectedMtrId={selectedMtrId}
          onSelect={setSelectedMaterialId}
          onRemove={handleRemove}
          showCommercialLinePricing={showCommercialLinePricing}
        />
        <StockReceiptMaterialSetting
          key={selectedMtrId || 'no-selection'}
          control={control}
          register={register}
          errors={errors}
          setValue={setValue}
          lineIndex={selectedLineIndex}
          mtrId={
            selectedLineIndex >= 0
              ? (linesForRender[selectedLineIndex]?.mtrId ?? '')
              : ''
          }
          mtrName={
            selectedLineIndex >= 0
              ? (linesForRender[selectedLineIndex]?.mtrName ?? '')
              : ''
          }
          mtrNo={
            selectedLineIndex >= 0
              ? (linesForRender[selectedLineIndex]?.mtrNo ?? '')
              : ''
          }
          warehouseId={warehouseId}
          locationOptions={locationOptionsQuery.data ?? []}
          locationOptionsLoading={locationOptionsQuery.isLoading}
          initialUnitOptions={
            selectedLineIndex >= 0
              ? (linesForRender[selectedLineIndex]?.unitOptions ?? null)
              : null
          }
          showCommercialLinePricing={showCommercialLinePricing}
        />
      </div>

      <MtrPickerDialog
        key={`mtr-picker-${pickerKey}`}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedRows={initialSelectedRows}
        onConfirm={handlePickerConfirm}
        title="請選擇入庫物料"
        description="多選後確認；已存在於明細者不重複加入。"
      />

      <ScanCodeDialog
        key={`original-serial-scan-${originalSerialScanKey}`}
        open={originalSerialScanOpen}
        onOpenChange={setOriginalSerialScanOpen}
        labels={ORIGINAL_SERIAL_SCAN_LABELS}
        lookup={lookupOriginalSerial}
        getItemId={getOriginalSerialItemId}
        renderScannedRow={renderOriginalSerialScannedRow}
        renderAmbiguousRow={renderOriginalSerialAmbiguousRow}
        onConfirm={handleOriginalSerialScanConfirm}
        getErrorMessage={(error) => getStockReceiptApiErrorMessage(
          error,
          '查詢原廠料號失敗',
        )}
      />
    </section>
  );
};

StockReceiptLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  showCommercialLinePricing: PropTypes.bool,
};

export default StockReceiptLinesEditor;
